<script lang="ts">
  import { type Image } from "bildebanken-model";
  import { isEmpty } from "lodash-es";
  import { onMount } from "svelte";
  import type { EditImageActor, EditImageState } from "./editImageMachine";
  import EditKeywords from "./EditKeywords.svelte";
  import { getAllChanges } from "./editImageMachine";
  import { uuidRe } from "utils/fns";
  import EditDescription from "./EditDescription.svelte";
  import EditVisibility from "./EditVisibility.svelte";
  import { pageState } from "../../state/page";
  import EditUsageDomain from "./EditUsageDomain.svelte";
  import { useFeatureToggle } from "../../services/configCatService";
  import EditRights from "./EditRights.svelte";
  import { detectLabels } from "services/cognitive";

  export let service: EditImageActor;
  export let state: EditImageState;
  export let image: Image;
  export let saveMetadata: () => void;

  let detectedLabels = image.detectedLabels || [];

  async function startLabelDetectionAndRefresh(imageId: string): Promise<void> {
    const refreshedLabels = await detectLabels(imageId);
    detectedLabels = refreshedLabels;
    service.send({
      type: "UPDATE_DETECTED_LABELS",
      imageId,
      detectedLabels: refreshedLabels,
    });
  }

  function saveOnCmdEnter(event: KeyboardEvent) {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      saveMetadata();
    }
  }

  function submitForm(event: SubmitEvent) {
    event.preventDefault();
    saveMetadata();
  }

  onMount(() => {
    document.addEventListener("keydown", saveOnCmdEnter);
    return () => document.removeEventListener("keydown", saveOnCmdEnter);
  });

  function fieldsChanged() {
    return getAllChanges(state).filter((change) => !isEmpty(change.metadataDelta)).length > 0;
  }

  function beforeUnload(event) {
    if (fieldsChanged()) {
      // Setting the returnValue is what enables the 'Unsaved changes' prompt
      event.returnValue = "";
    }
  }

  // Strip out auto generated GUID title, such as when importing Polopoly images without title
  if (image.metadata.title?.substring(0, 36).match(uuidRe)) {
    image.metadata.title = "";
  }
</script>

<svelte:window on:beforeunload|preventDefault={beforeUnload} />
<div class="wrapper">
  <form id="edit-metadata-form" on:submit={submitForm}>
    <h2>{$pageState.type === "UPLOAD" ? "Legg til metadata" : "Rediger metadata"}</h2>
    <p>
      Fyll ut feltene under for å sikre at innholdet kan gjenfinnes, brukes riktig og at evt.
      rettigheter blir ivaretatt.
    </p>
    {#await useFeatureToggle("usageDomain") then featureEnabled}
      {#if featureEnabled}
        <EditUsageDomain {service} {image} />
      {/if}
    {/await}
    <EditDescription {state} {service} {image} />
    <h3>Vilkår og kreditering</h3>
    <EditRights {service} {image} />
    <h3>Tilgangsstyring</h3>
    <EditVisibility {service} {image} />
    <h3>Tagger</h3>
    <EditKeywords
      {service}
      _startLabelDetection={() => startLabelDetectionAndRefresh(image.id)}
      keywords={image.metadata.keywords || []}
      {detectedLabels}
    />
    <!-- <PeopleInImageField {state} {service} /> -->
  </form>
</div>

<style>
  h2 {
    font-weight: 500;
    font-size: 24px;
  }
  h3 {
    font-weight: 500;
    font-size: 18px;
  }
  .wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--org-medium);
    padding: var(--org-small) var(--org-medium);
  }
  p {
    margin-bottom: var(--org-medium);
  }
</style>
